import React, { useState, useEffect } from 'react';
import { Star, MessageCircle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReviewsList({ productId, currentUserId, onReviewDeleted }) {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const limit = 10;

    const fetchReviews = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_URL}/reviews/product/${productId}?skip=${page * limit}&limit=${limit}`
            );

            if (page === 0) {
                setReviews(response.data);
            } else {
                setReviews(prev => [...prev, ...response.data]);
            }

            setHasMore(response.data.length === limit);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            toast.error('Erreur lors du chargement des avis');
        } finally {
            setLoading(false);
        }
    }, [productId, page]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const handleDelete = async (reviewId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/reviews/${reviewId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setReviews(prev => prev.filter(r => r.id !== reviewId));
            toast.success('Avis supprimé avec succès');

            if (onReviewDeleted) {
                onReviewDeleted();
            }
        } catch (error) {
            console.error('Error deleting review:', error);
            toast.error('Erreur lors de la suppression de l\'avis');
        }
    };

    const renderStars = (rating) => {
        return (
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={`h-4 w-4 ${star <= rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                            }`}
                    />
                ))}
            </div>
        );
    };

    if (loading && page === 0) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (reviews.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucun avis pour le moment</p>
                <p className="text-sm mt-2">Soyez le premier à laisser un avis !</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {reviews.map((review) => (
                <Card key={review.id} className="overflow-hidden">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                        <span className="text-orange-600 font-semibold">
                                            {review.user_name?.charAt(0) || 'U'}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">
                                            {review.user_name || 'Utilisateur'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatDistanceToNow(new Date(review.created_at), {
                                                addSuffix: true,
                                                locale: fr
                                            })}
                                        </p>
                                    </div>
                                </div>
                                {renderStars(review.rating)}
                            </div>

                            {currentUserId && currentUserId === review.user_id && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(review.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>

                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {review.comment}
                        </p>
                    </CardContent>
                </Card>
            ))}

            {hasMore && (
                <div className="flex justify-center pt-4">
                    <Button
                        variant="outline"
                        onClick={() => setPage(p => p + 1)}
                        disabled={loading}
                    >
                        {loading ? 'Chargement...' : 'Voir plus d\'avis'}
                    </Button>
                </div>
            )}
        </div>
    );
}
